import type React from "react";

import classNames from "classnames";

interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  children: React.ReactNode;
  type?:
  | "primary"
  | "secondary"
  | "accent"
  | "error"
  | "base"
  | "info"
  | "success"
  | "warning";
  variant?: "filled" | "outlined" | "outline" | "ghost";
  size?: "xs" | "sm" | "md" | "lg";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  htmlType?: "button" | "submit" | "reset";
  dataCy?: string;
}

type ButtonColorType = "primary" | "secondary" | "accent" | "error" | "base";
type ButtonType = NonNullable<ButtonProps["type"]>;
type ButtonVariant = NonNullable<ButtonProps["variant"]>;

const BUTTON_TYPE_ALIASES: Record<ButtonType, ButtonColorType> = {
  primary: "primary",
  secondary: "secondary",
  accent: "accent",
  error: "error",
  base: "base",
  info: "primary",
  success: "accent",
  warning: "secondary",
};

const BUTTON_VARIANTS = {
  primary: {
    filled:
      "bg-primary-500 text-white shadow-sm hover:bg-primary-600 dark:bg-primary-400 dark:text-content-950 dark:hover:bg-primary-300 border border-transparent",
    outlined:
      "border-primary-300 bg-white text-primary-700 shadow-sm hover:bg-primary-50 dark:border-primary-500/60 dark:bg-content-900/30 dark:text-primary-300 dark:hover:bg-primary-500/15",
    ghost:
      "border-transparent bg-transparent text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-500/15",
  },
  secondary: {
    filled:
      "bg-secondary-500 text-white shadow-sm hover:bg-secondary-600 dark:bg-secondary-400 dark:text-content-950 dark:hover:bg-secondary-300 border border-transparent",
    outlined:
      "border-secondary-300 bg-white text-secondary-700 shadow-sm hover:bg-secondary-50 dark:border-secondary-600 dark:bg-transparent dark:text-secondary-300 dark:hover:bg-secondary-900/50",
    ghost:
      "border-transparent bg-transparent text-secondary-700 hover:bg-secondary-50 dark:text-secondary-300 dark:hover:bg-secondary-900/50",
  },
  accent: {
    filled:
      "bg-accent-500 text-white shadow-sm hover:bg-accent-600 dark:bg-accent-600 dark:hover:bg-accent-500 border border-transparent",
    outlined:
      "border-accent-300 bg-white text-accent-700 shadow-sm hover:bg-accent-50 dark:border-accent-600 dark:bg-transparent dark:text-accent-300 dark:hover:bg-accent-900/50",
    ghost:
      "border-transparent bg-transparent text-accent-700 hover:bg-accent-50 dark:text-accent-300 dark:hover:bg-accent-900/50",
  },
  error: {
    filled:
      "bg-error-500 text-white shadow-sm hover:bg-error-600 dark:bg-error-600 dark:hover:bg-error-500 border border-transparent",
    outlined:
      "border-error-300 bg-white text-error-700 shadow-sm hover:bg-error-50 dark:border-error-600 dark:bg-transparent dark:text-error-300 dark:hover:bg-error-900/50",
    ghost:
      "border-transparent bg-transparent text-error-700 hover:bg-error-50 dark:text-error-300 dark:hover:bg-error-900/50",
  },
  base: {
    filled:
      "bg-base-100 text-content-800 shadow-sm hover:bg-base-50 dark:bg-content-800 dark:text-content-100 dark:hover:bg-content-700 border border-content-200 dark:border-content-700/70",
    outlined:
      "border-content-300 bg-base-200 text-content-700 shadow-sm hover:bg-base-50 dark:border-content-700/70 dark:bg-content-900/30 dark:text-content-300 dark:hover:bg-content-800",
    ghost:
      "border-transparent bg-base-50 text-content-700 hover:bg-base-100 dark:text-content-300 dark:hover:bg-content-800",
  },
} as const;

const BUTTON_SIZE_CLASSES = {
  xs: "min-h-7 px-2.5 text-xs md:min-h-8",
  sm: "min-h-9 px-3 text-xs md:min-h-10",
  md: "min-h-10 px-4 text-sm md:min-h-11",
  lg: "min-h-11 px-5 text-sm md:min-h-12 md:px-6 md:text-base",
} as const;

export const Button = ({
  id,
  children,
  type = "primary",
  variant = "filled",
  size = "md",
  startIcon,
  endIcon,
  loading = false,
  htmlType,
  className,
  disabled = false,
  dataCy,
  ...props
}: ButtonProps) => {
  const resolvedType = BUTTON_TYPE_ALIASES[type] || "primary";
  const resolvedVariant: Exclude<ButtonVariant, "outline"> =
    variant === "outline" ? "outlined" : variant;
  const isIconOnly = !children && (startIcon || endIcon);
  const computedDisabled = disabled || loading;

  return (
    <button
      id={id}
      data-cy={dataCy}
      disabled={computedDisabled}
      type={htmlType}
      className={classNames(
        "cursor-pointer",
        "group relative inline-flex items-center justify-center overflow-hidden rounded-lg font-sans font-medium",
        "transition-all duration-200",
        "text-center",
        BUTTON_SIZE_CLASSES[size],
        isIconOnly &&
        (size === "xs" ? "w-7 md:w-8 p-0" : size === "sm" ? "w-9 p-0" : size === "lg" ? "w-11 md:w-12 p-0" : "w-10 md:w-11 p-0"),
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/55 focus-visible:ring-offset-2 focus-visible:ring-offset-white/30",
        "dark:focus-visible:ring-primary-400/55 dark:focus-visible:ring-offset-content-950/70",
        "disabled:pointer-events-none disabled:opacity-45 disabled:saturate-50",
        BUTTON_VARIANTS[resolvedType][resolvedVariant],
        className
      )}
      {...props}
    >


      <div className="relative flex-row inline-flex space-x-2 items-center justify-center gap-2">
        {loading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
        ) : (
          startIcon
        )}
        {children ? <div className="flex-row inline-flex truncate space-x-2">{children}</div> : null}
        {!loading && endIcon}
      </div>
    </button>
  );
};
